<script setup>
import { ref, onMounted } from 'vue'
import { getI18n } from '@/plugins/i18n'

import { usePlanningStore } from '@/stores/planning.store'
import { infoBoxIdEnum } from '@/stores/portal.store'

import { useNotify } from '@/composables/notify'
import { useStepper } from '@/composables/stepper'
import { useIsUpdated } from '@/composables/object'
import { useNavigator } from '@/composables/navigator'
import { useEscapeClose } from '@/composables/keyboard'
import { useDetectDevice } from '@/composables/device'
import { useArchive } from '@/composables/archive'
import { useClone } from '@/composables/object'
import { useValidation } from '@/composables/validation'
import { useWarehouseDeleteOptions } from '@/composables/delete-options'

import NavigateBack from '@/components/shared/NavigateBack.vue'
import StepperNavigation from '@/components/shared/StepperNavigation.vue'
import ArchiveWarningDialog from '@/components/shared/ArchiveWarningDialog.vue'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import DeleteOptionsDialog from '@/components/shared/DeleteOptionsDialog.vue'
import CountrySelect from '@/components/shared/CountrySelect.vue'
import InfoBox from '@/components/shared/InfoBox.vue'

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
})

const i18n = getI18n()
const { isMobile } = useDetectDevice()
const { step, nextStep, prevStep, markAttempted } = useStepper()
const { notifySuccess, notifyError } = useNotify()
const { emailValidator } = useValidation()

const planningStore = usePlanningStore()

const form = ref(null)
const editItem = ref(Object.keys(props.modelValue).length > 0 ? useClone(props.modelValue) : {
  name: null,
  description: null,
  street: null,
  street_number: null,
  zip_code: null,
  city: null,
  country: null,
  phone: null,
  email: null,
  is_virtual: false,
})

const { isUpdated, isUpdating, setOriginal } = useIsUpdated(editItem)

onMounted(async () => {
  if (props.modelValue.id) {
    setOriginal(editItem.value)
  }
})

const { deleteOptions, onDeleteOptionSelect, onWarehouseBlocked } = useWarehouseDeleteOptions({
  onDeleted: () => emit('delete'),
})

const { archiveWarning, archiveAsync, onRowRemoving: onDelete } = useArchive({
  onArchive: async (id) => { await planningStore.archiveWarehouseAsync(id) },
  onDelete: async (id, config) => {
    await planningStore.deleteWarehouseAsync(id, config)
    notifySuccess(i18n.t('info.deletedSuccessfully'))
    emit('delete')
  },
  onDeleteBlocked: onWarehouseBlocked,
  onArchiveBlocked: onWarehouseBlocked,
})

const onUpdate = async () => {
  isUpdating.value = true
  try {
    const saved = await planningStore.upsertWarehouseAsync({ ...editItem.value })
    notifySuccess(editItem.value.id ? i18n.t('info.updatedSuccessfully') : i18n.t('info.createdSuccessfully'))
    editItem.value.id = editItem.value.id || saved.data.id
    emit('update', editItem.value)
  } catch (error) {
    console.error(error)
    notifyError(i18n.t('error.saveFailed'))
  } finally {
    isUpdating.value = false
  }
}

const showDeleteDialog = ref(false)
const onConfirmDelete = () => { showDeleteDialog.value = true }

const emit = defineEmits(['update', 'cancel', 'delete'])
useNavigator(() => emit('cancel'))
useEscapeClose(isUpdated, () => emit('cancel'))
const onCancel = () => emit('cancel')

const hasErrors = () => !editItem.value.name

</script>
<template>
  <q-card v-drag-dialog="!isMobile" class="dialog-card-flex">
    <archive-warning-dialog
      v-model="archiveWarning"
      @confirm="async () => {
        await archiveAsync(archiveWarning.item.id, false)
        emit('update')
      }"
    />
    <confirm-dialog
      v-model="showDeleteDialog"
      :message="i18n.t('common.confirmDelete')"
      icon="mdi-delete-outline"
      icon-color="negative"
      @confirm="onDelete({ id: editItem.id })"
    />
    <delete-options-dialog
      v-model="deleteOptions.show"
      :title="deleteOptions.title"
      :message="deleteOptions.message"
      :caption="deleteOptions.caption"
      :options="deleteOptions.options"
      @select="onDeleteOptionSelect"
    />
    <navigate-back
      :text="editItem?.id ? i18n.t('common.editWarehouse') : i18n.t('common.addWarehouse')"
      @cancel="onCancel"
    />
    <q-card-section class="scroll col q-pa-none dialog-card-content-scroll" :class="{ 'dialog-card-content': editItem?.id }">
      <q-card-section class="q-px-sm q-pt-none">
        <q-form ref="form">
          <q-stepper v-model="step" class="q-py-none">
            <q-step
              :name="1"
              :title="i18n.t('common.basicInfo')"
              icon="mdi-folder-plus"
              :done="!!editItem.id || step > 1"
              :header-nav="!!editItem.id || step > 1"
            >
              <div class="row q-col-gutter-sm">
                <q-input
                  v-model="editItem.name"
                  :class="isMobile ? 'col-12' : 'col-6'"
                  :label="i18n.t('common.name')"
                  autofocus
                  required
                  :rules="[val => val && val.length > 0 || i18n.t('common.required')]"
                />
                <q-input
                  v-model="editItem.phone"
                  :class="isMobile ? 'col-12' : 'col-3'"
                  :label="i18n.t('common.phone')"
                  type="tel"
                />
                <q-input
                  v-model="editItem.email"
                  :class="isMobile ? 'col-12' : 'col-3'"
                  :label="i18n.t('common.email')"
                  type="email"
                  lazy-rules
                  :rules="[val => !val || emailValidator(val) || i18n.t('common.notValid')]"
                >
                  <template #prepend>
                    <q-icon name="mdi-email" />
                  </template>
                </q-input>
                <q-input
                  v-model="editItem.description"
                  class="col-12"
                  :label="i18n.t('common.description')"
                  autogrow
                />
                <q-toggle
                  v-model="editItem.is_virtual"
                  class="col-12"
                  :label="i18n.t('common.virtualLocation')"
                  color="primary"
                />
              </div>
              <info-box
                v-if="editItem.is_virtual"
                :id="infoBoxIdEnum.VIRTUAL_LOCATION"
                class="q-mt-md"
                :message="i18n.t('common.virtualLocationHint')"
              />
              <stepper-navigation
                :continue="editItem.is_virtual ? null : () => { markAttempted(1); form.validate().then(success => success && nextStep()) }"
                :finish="editItem.is_virtual && !editItem.id ? () => { markAttempted(1); form.validate().then(success => !hasErrors() && success && onUpdate()) } : null"
                :finish-label="i18n.t('common.addWarehouse')"
                :loading="isUpdating"
              />
            </q-step>
            <q-step
              :name="2"
              :title="i18n.t('common.address')"
              icon="mdi-map-marker"
              :done="!!editItem.id || step > 2"
              :header-nav="!!editItem.id || step > 2"
              :disable="editItem.is_virtual"
            >
              <div class="row q-col-gutter-sm">
                <country-select
                  v-model="editItem.country"
                  :class="isMobile ? 'col-12' : 'col-6'"
                />
                <q-input
                  v-model="editItem.city"
                  :class="isMobile ? 'col-12' : 'col-6'"
                  :label="i18n.t('common.city')"
                />
                <q-input
                  v-model="editItem.zip_code"
                  :class="isMobile ? 'col-12' : 'col-3'"
                  :label="i18n.t('common.zipCode')"
                />
                <q-input
                  v-model="editItem.street"
                  :class="isMobile ? 'col-12' : 'col-6'"
                  :label="i18n.t('common.street')"
                />
                <q-input
                  v-model="editItem.street_number"
                  :class="isMobile ? 'col-12' : 'col-3'"
                  :label="i18n.t('common.streetNumber')"
                />
              </div>
              <stepper-navigation
                :back="prevStep"
                :finish="editItem.id ? null : () => { markAttempted(2); form.validate().then(success => !hasErrors() && success && onUpdate()) }"
                :finish-label="i18n.t('common.addWarehouse')"
                :loading="isUpdating"
              />
            </q-step>
          </q-stepper>
        </q-form>
      </q-card-section>
    </q-card-section>
    <q-card-actions v-if="editItem?.id" class="align-center dialog-card-bottom-bar">
      <q-btn
        class="large-btn q-mr-sm"
        color="negative"
        :label="i18n.t('common.delete')"
        @click="onConfirmDelete"
      />
      <q-btn
        v-if="isUpdated"
        class="large-btn"
        color="positive"
        :disable="!editItem.name"
        :label="i18n.t('common.update')"
        :loading="isUpdating"
        @click="() => form.validate().then(success => !hasErrors() && success && onUpdate())"
      />
    </q-card-actions>
  </q-card>
</template>
